import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useEnumString } from '@hooks/useEnumString';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { EnumSortDirection } from '@hooks/useRequest';
import { useValidation } from '@hooks/useValidation';
import { EnumEmpresa, useEmpresaCombo } from '@model/Empresa';
import { FaturaTipo } from '@model/Fatura';
import { useFaturaService } from '@services/FaturaService';
import { IRelatorioValoresEmitidosFilter, IRelatorioValoresEmitidosRecord, useRelatorioValoresEmitidosService } from '@services/Relatorios/ValoresEmitidosService';
import { lastCompetence } from '@utils/Functions';
import { yup } from '@validations/Yup';
import React from 'react';
import { RelatorioValoresEmitidosFilter } from './Filter';
import { RelatorioValoresEmitidosTable } from './Table';

export type RelatorioValoresEmitidosProps = {
}

export function RelatorioValoresEmitidos (props: RelatorioValoresEmitidosProps) {
    const data = useData<IRelatorioValoresEmitidosRecord, IRelatorioValoresEmitidosFilter>({
        filter: {
            competencia: useField(lastCompetence),
            empresaId: useField(EnumEmpresa.None),
            tipo: useField()
        },

        sortField: {
            name: "empresaNome",
            direction: EnumSortDirection.Asc
        }
    })

    const loading = useLoading();
    const message = useMessage();

    const relatorioValoresEmitidosService = useRelatorioValoresEmitidosService();
    const faturaService = useFaturaService();

    const empresaCombo = useEmpresaCombo();
    const faturaTipoCombo = useEnumString<FaturaTipo>();

    const validation = useValidation<IRelatorioValoresEmitidosFilter>({
        competencia: yup.string().required()
    })

    const handleCsvButtonClick = React.useCallback(() => {
        if (!loading.isLoading) {
            data.filter.clearErrors();
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioValoresEmitidosService.exportar(data.requestedData)
                    .catch(message.showBlobException)
                    .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
            }
    }, [data.filter, data.requestedData, loading, message.showBlobException, relatorioValoresEmitidosService, validation]);

    React.useEffect(() => {
        if (!loading.isLoading) {
            data.filter.clearErrors();
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioValoresEmitidosService.listar(data.requestedData)
                        .then(data.handleResultResponse)
                        .catch(message.showException)
                        .finally(loading.stop);
                        })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();
        faturaTipoCombo.load(faturaService.listarTipos);
    });

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Relatório de Valores Emitidos" />
                </PageTitle>

                <PageContent>
                    <RelatorioValoresEmitidosFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        empresaCombo={empresaCombo}
                        faturaTipoCombo={faturaTipoCombo}
                    />
                    
                    <RelatorioValoresEmitidosTable 
                        data={data}
                        isLoading={loading.isLoading}
                        onCsvButtonClick={handleCsvButtonClick}
                    />
                </PageContent>
            </Main>
        </React.Fragment>
    )
}